import { forwardRef, useRef } from 'react'
import type { ButtonHTMLAttributes, MouseEvent, PointerEvent, ReactNode } from 'react'
import type { GlobalActionLoadingOptions } from '../../context/globalActionLoadingContextCore'
import { useOptionalGlobalActionLoading } from '../../context/useGlobalActionLoading'
import { emitZap } from './zapRipple'

export type ZapButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link' | 'danger'
export type ZapButtonSize = 'sm' | 'md' | 'lg'
export type ZapButtonTone = 'default' | 'danger'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ZapButtonVariant
  size?: ZapButtonSize
  tone?: ZapButtonTone
  icon?: ReactNode
  trailingIcon?: ReactNode
  loading?: boolean
  fullWidth?: boolean
  globalLoading?: boolean | GlobalActionLoadingOptions
  pressed?: boolean
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = 'primary',
    size = 'md',
    tone = 'default',
    icon,
    trailingIcon,
    loading = false,
    fullWidth = false,
    globalLoading = false,
    disabled,
    pressed,
    className,
    children,
    onPointerDown,
    onClick,
    ...rest
  },
  ref,
) {
  const localRef = useRef<HTMLButtonElement | null>(null)
  const globalActionLoading = useOptionalGlobalActionLoading()
  const globallyDisabled = Boolean(globalLoading && globalActionLoading?.isActionLoading)

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event)
    if (event.defaultPrevented || disabled || loading || globallyDisabled) return
    if (variant === 'primary') {
      const el = event.currentTarget
      const rect = el.getBoundingClientRect()
      emitZap(el, event.clientX - rect.left, event.clientY - rect.top)
    }
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading || globallyDisabled) {
      event.preventDefault()
      return
    }
    const result = onClick?.(event) as unknown
    if (
      globalLoading &&
      result &&
      typeof (result as Promise<unknown>).then === 'function' &&
      globalActionLoading
    ) {
      const options = typeof globalLoading === 'object' ? globalLoading : undefined
      void globalActionLoading.runWithGlobalLoading(() => result as Promise<unknown>, options)
    }
  }

  const variantClass = variant === 'danger' ? 'zapBtn--dangerSolid' : `zapBtn--${variant}`

  const classes = [
    'zapBtn',
    variantClass,
    `zapBtn--${size}`,
    tone === 'danger' ? 'zapBtn--danger' : '',
    pressed ? 'is-pressed' : '',
    fullWidth ? 'zapBtn--full' : '',
    loading ? 'is-loading' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={(node) => {
        localRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as { current: HTMLButtonElement | null }).current = node
      }}
      type={rest.type ?? 'button'}
      className={classes}
      disabled={disabled || loading || globallyDisabled}
      aria-pressed={pressed !== undefined ? pressed : rest['aria-pressed']}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...rest}
    >
      <span className="zapBtn__glow" aria-hidden="true" />
      <span className="zapBtn__charge" aria-hidden="true" />
      {loading && (
        <span className="zapBtn__spark" aria-hidden="true">
          <span className="zapBtn__sparkCore" />
          <span className="zapBtn__sparkArc zapBtn__sparkArc--a" />
          <span className="zapBtn__sparkArc zapBtn__sparkArc--b" />
        </span>
      )}
      {!loading && icon && <span className="zapBtn__icon zapBtn__icon--lead" aria-hidden="true">{icon}</span>}
      <span className="zapBtn__label">{children}</span>
      {!loading && trailingIcon && <span className="zapBtn__icon zapBtn__icon--trail" aria-hidden="true">{trailingIcon}</span>}
    </button>
  )
})
